"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { useAuthStore } from "@/stores/authStore";

export type GrnStatus = "draft" | "completed";

export type GrnItemRow = {
  id: number;
  grn_id: number;
  product_id: number;
  product_name: string;
  qty_ordered: number;
  qty_received: number;
  rate: number;
  gst_rate: number;
  batch_no?: string | null;
  mfg_date?: string | null;
  expiry_date?: string | null;
  price_variance_pct: number;
  amount: number;
};

export type GrnRow = {
  id: number;
  grn_no: string;
  grn_date: string;
  po_id?: number | null;
  supplier_id: number;
  branch_id: number;
  status: GrnStatus;
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  notes?: string | null;
};

export type GrnDetail = GrnRow & { items: GrnItemRow[] };

export function useGrns(params: { branch_id?: number; supplier_id?: number; po_id?: number }) {
  const token = useAuthStore((s) => s.accessToken);

  return useQuery({
    queryKey: ["grns", params],
    queryFn: async () => {
      const { data } = await api.get<{ grns: GrnRow[] }>("/api/grns", {
        params: {
          branch_id: params.branch_id,
          supplier_id: params.supplier_id,
          po_id: params.po_id,
        },
      });

      return data.grns;
    },
    enabled: Boolean(token),
  });
}

export function useGrn(id: number | null) {
  const token = useAuthStore((s) => s.accessToken);

  return useQuery({
    queryKey: ["grn", id],
    queryFn: async () => {
      const { data } = await api.get<{ grn: GrnDetail }>(`/api/grns/${id}`);

      return data.grn;
    },
    enabled: Boolean(token) && id != null && id > 0,
  });
}

export function useGrnMutations() {
  const qc = useQueryClient();

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["grns"] });
    void qc.invalidateQueries({ queryKey: ["grn"] });
    void qc.invalidateQueries({ queryKey: ["purchase-orders"] });
  };

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: number; body: Record<string, unknown> }) => {
      const { data } = await api.put<{ grn: GrnDetail }>(`/api/grns/${id}`, body);

      return data.grn;
    },
    onSuccess: invalidate,
  });

  const complete = useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ grn: GrnDetail }>(`/api/grns/${id}/complete`);

      return data.grn;
    },
    onSuccess: invalidate,
  });

  return { update, complete };
}
